// Тренировки, шаблоны и справочник упражнений: типы ответов /api и запросы TanStack Query.
// Общий модуль для экранов /workouts, /templates, /exercises (и экрана тренировки в зале).

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/api/client'

export type Equipment = 'barbell' | 'dumbbell' | 'smith' | 'machine' | 'cable' | 'bodyweight' | 'kettlebell' | 'band' | 'other'
export type Tracking = 'weight_reps' | 'bodyweight_reps' | 'time' | 'distance'

export type Exercise = {
  id: string
  name: string
  primary_muscle: string
  secondary_muscles: string[]
  equipment: Equipment
  tracking: Tracking
  garmin_category: string | null
  garmin_name: string | null
  is_custom: boolean
  archived: boolean
  notes: string | null
  /** Техника: шаги по одному на строку. */
  description: string | null
  /** Имена файлов фото (free-exercise-db) — адрес даёт exerciseImageUrl. */
  images: string[]
}

export type ExerciseInput = {
  name: string
  primary_muscle: string
  secondary_muscles: string[]
  equipment: Equipment
  tracking: Tracking
  garmin_category: string | null
  garmin_name: string | null
  notes: string | null
  description: string | null
}

export type Muscle = { key: string; label_ru: string }

export type TargetSet = {
  reps: number | null
  weight_kg: number | null
  duration_s: number | null
  distance_m: number | null
  rest_s: number | null
}

export type Progression = { enabled: boolean; increment_kg: number }

export type TemplateExercise = {
  id: string
  position: number
  exercise_id: string
  exercise_name: string
  tracking: Tracking
  exercise_archived: boolean
  target_sets: TargetSet[]
  progression: Progression
  notes: string | null
}

export type Template = {
  id: string
  name: string
  notes: string | null
  archived: boolean
  exercises: TemplateExercise[]
  updated_at: string
}

export type TemplateInput = {
  name: string
  notes: string | null
  exercises: {
    id: string | null
    exercise_id: string
    target_sets: TargetSet[]
    progression: Progression
    notes: string | null
  }[]
}

export type PrFlags = Partial<Record<'weight' | 'e1rm' | 'volume' | 'reps' | 'duration' | 'distance', boolean>>

export type WorkoutSet = {
  id: string
  session_id: string
  exercise_id: string
  exercise_position: number
  set_index: number
  reps: number | null
  weight_kg: number | null
  duration_s: number | null
  distance_m: number | null
  is_warmup: boolean
  completed: boolean
  completed_at: string | null
  rest_s: number | null
  is_pr: PrFlags | null
}

export type SetInput = Omit<WorkoutSet, 'id' | 'session_id' | 'is_pr'>

export type SessionExercise = {
  exercise_id: string
  position: number
  name: string
  tracking: Tracking
  archived: boolean
}

export type Session = {
  id: string
  started_at: string
  ended_at: string | null
  template_id: string | null
  template_name: string | null
  planned_session_id: string | null
  notes: string | null
  auto_closed: boolean
  duration_s: number | null
  volume_kg: number
  sets_done: number
  exercises: SessionExercise[]
  sets: WorkoutSet[]
}

export type SessionInput = {
  started_at: string
  ended_at: string | null
  template_id: string | null
  notes: string | null
}

export type SessionSummary = {
  id: string
  started_at: string
  ended_at: string | null
  template_id: string | null
  template_name: string | null
  auto_closed: boolean
  duration_s: number | null
  volume_kg: number
  sets_done: number
  exercise_names: string[]
  pr_count: number
}

export type SessionPage = { items: SessionSummary[]; next_cursor: string | null }

export type RecordValue = {
  value: number
  set_id: string
  session_id: string
  achieved_at: string
  weight_kg: number | null
  reps: number | null
}

export type Records = {
  exercise_id: string
  weight: RecordValue | null
  e1rm: RecordValue | null
  volume: RecordValue | null
  reps: RecordValue | null
  duration: RecordValue | null
  distance: RecordValue | null
}

export type ProgressPoint = {
  session_id: string
  day: string
  started_at: string
  weight_kg: number | null
  reps: number | null
  e1rm: number | null
  volume_kg: number
  duration_s: number | null
  distance_m: number | null
}

/** Шаблон по тренировке: что изменится в шаблоне (GET /sessions/{id}/template). */
export type TemplateChange = {
  kind: 'added' | 'removed' | 'changed' | 'progressed'
  exercise_name: string
  before: string | null
  after: string | null
}

export type TemplateSyncPreview = {
  mode: 'update' | 'create'
  template_id: string | null
  template_name: string | null
  suggested_name: string
  changes: TemplateChange[]
  reordered: boolean
  unchanged: number
}

export type SessionFilters = { template_id?: string; exercise_id?: string }

export const trainingKeys = {
  all: ['training'] as const,
  muscles: ['training', 'muscles'] as const,
  exercises: (q: string, muscle: string, archived: boolean) => ['training', 'exercises', q, muscle, archived] as const,
  exercise: (id: string) => ['training', 'exercise', id] as const,
  records: (id: string) => ['training', 'records', id] as const,
  progress: (id: string) => ['training', 'progress', id] as const,
  last: (id: string) => ['training', 'last', id] as const,
  templates: (archived: boolean) => ['training', 'templates', archived] as const,
  template: (id: string) => ['training', 'template', id] as const,
  sessions: (filters: SessionFilters) => ['training', 'sessions', filters] as const,
  session: (id: string) => ['training', 'session', id] as const,
}

export const trainingApi = {
  muscles: () => api.get<Muscle[]>('/exercises/muscles'),
  exercises: (q: string, muscle: string, includeArchived: boolean) =>
    api.get<Exercise[]>('/exercises', { query: { q, muscle, include_archived: includeArchived || undefined } }),
  exercise: (id: string) => api.get<Exercise>(`/exercises/${id}`),
  createExercise: (data: ExerciseInput) => api.post<Exercise>('/exercises', data),
  updateExercise: (id: string, data: Partial<ExerciseInput> & { archived?: boolean }) =>
    api.patch<Exercise>(`/exercises/${id}`, data),
  describeExercise: (id: string) => api.post<Exercise>(`/exercises/${id}/describe`, {}),
  records: (id: string) => api.get<Records>(`/exercises/${id}/records`),
  progress: (id: string) => api.get<ProgressPoint[]>(`/exercises/${id}/progress`),
  last: (id: string, before?: string) => api.get<WorkoutSet[]>(`/exercises/${id}/last`, { query: { before } }),
  templates: (includeArchived: boolean) =>
    api.get<Template[]>('/templates', { query: { include_archived: includeArchived || undefined } }),
  template: (id: string) => api.get<Template>(`/templates/${id}`),
  createTemplate: (data: TemplateInput) => api.post<Template>('/templates', data),
  updateTemplate: (id: string, data: TemplateInput) => api.put<Template>(`/templates/${id}`, data),
  copyTemplate: (id: string) => api.post<Template>(`/templates/${id}/copy`, {}),
  archiveTemplate: (id: string, archived: boolean) => api.post<Template>(`/templates/${id}/archive`, { archived }),
  templateWarnings: (id: string, day?: string) => api.get<string[]>(`/templates/${id}/warnings`, { query: { day } }),
  sessions: (filters: SessionFilters, cursor?: string | null) =>
    api.get<SessionPage>('/sessions', { query: { ...filters, cursor, limit: 30 } }),
  session: (id: string) => api.get<Session>(`/sessions/${id}`),
  putSession: (id: string, data: SessionInput) => api.put<Session>(`/sessions/${id}`, data),
  patchSession: (id: string, data: Partial<SessionInput>) => api.patch<Session>(`/sessions/${id}`, data),
  finishSession: (id: string, endedAt?: string) =>
    api.post<Session>(`/sessions/${id}/finish`, endedAt ? { ended_at: endedAt } : {}),
  deleteSession: (id: string) => api.delete(`/sessions/${id}`),
  putSet: (sessionId: string, setId: string, data: SetInput) =>
    api.put<WorkoutSet>(`/sessions/${sessionId}/sets/${setId}`, data),
  deleteSet: (sessionId: string, setId: string) => api.delete(`/sessions/${sessionId}/sets/${setId}`),
  sessionTemplate: (id: string) => api.get<TemplateSyncPreview>(`/sessions/${id}/template`),
  applySessionTemplate: (id: string, name?: string) => api.post<Template>(`/sessions/${id}/template`, { name }),
}

/** Адрес фото упражнения; service worker держит их в отдельном кэше — видны и без сети. */
export function exerciseImageUrl(name: string): string {
  return `/api/exercise-images/${encodeURIComponent(name)}`
}

/** Шаги техники из описания (по одному на строку). */
export function techniqueSteps(description: string | null): string[] {
  return (description ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
}

export function useMuscles() {
  return useQuery({ queryKey: trainingKeys.muscles, queryFn: trainingApi.muscles, staleTime: Infinity })
}

/** Подпись группы мышц по ключу: 'chest' → «Грудь». */
export function useMuscleLabels(): (key: string) => string {
  const muscles = useMuscles()
  return (key: string) => muscles.data?.find((m) => m.key === key)?.label_ru ?? key
}

export function useExercises(q = '', muscle = '', includeArchived = false) {
  return useQuery({
    queryKey: trainingKeys.exercises(q, muscle, includeArchived),
    queryFn: () => trainingApi.exercises(q, muscle, includeArchived),
    placeholderData: (previous) => previous,
  })
}

/** Упражнение из справочника (он в кэше и без сети); undefined — пока не загружен. */
export function useCatalogExercise(exerciseId: string): Exercise | undefined {
  return useExercises().data?.find((e) => e.id === exerciseId)
}

/** Основная группа мышц упражнения из справочника; null — пока не загружен. */
export function useExerciseMuscle(exerciseId: string): string | null {
  const label = useMuscleLabels()
  const key = useCatalogExercise(exerciseId)?.primary_muscle
  return key ? label(key) : null
}

export function useExercise(id: string) {
  return useQuery({ queryKey: trainingKeys.exercise(id), queryFn: () => trainingApi.exercise(id) })
}

export function useTemplates(includeArchived = false) {
  return useQuery({
    queryKey: trainingKeys.templates(includeArchived),
    queryFn: () => trainingApi.templates(includeArchived),
  })
}

export function useTemplate(id: string | null) {
  return useQuery({
    queryKey: trainingKeys.template(id ?? ''),
    queryFn: () => trainingApi.template(id ?? ''),
    enabled: Boolean(id),
  })
}

export function useSessions(filters: SessionFilters) {
  return useInfiniteQuery({
    queryKey: trainingKeys.sessions(filters),
    queryFn: ({ pageParam }) => trainingApi.sessions(filters, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  })
}

export function useSession(id: string | null) {
  return useQuery({
    queryKey: trainingKeys.session(id ?? ''),
    queryFn: () => trainingApi.session(id ?? ''),
    enabled: Boolean(id),
  })
}

/** После любой записи тренировок — сбросить кэш области (история, рекорды, шаблоны). */
export function useInvalidateTraining() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: trainingKeys.all })
}

export function useSessionTemplatePreview(id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...trainingKeys.session(id), 'template'],
    queryFn: () => trainingApi.sessionTemplate(id),
    enabled,
  })
}

/** Обновить шаблон по тренировке (или создать новый); цели плана тоже пересчитываются. */
export function useApplySessionTemplate(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (name?: string) => trainingApi.applySessionTemplate(id, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: trainingKeys.all })
      void queryClient.invalidateQueries({ queryKey: ['plan'] })
    },
  })
}

export function useDeleteSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => trainingApi.deleteSession(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: trainingKeys.session(id) })
      void queryClient.invalidateQueries({ queryKey: trainingKeys.all })
    },
  })
}
